/**
 * MicVisualizer — realtime microphone level visualization.
 *
 * Renders an animated "voice graph" (frequency bars) driven by the live mic
 * input while a roleplay session is capturing, so the agent has clear visual
 * confirmation that the app is hearing them. Self-contained: opens its own
 * getUserMedia stream + AnalyserNode and fully tears them down when inactive,
 * so it never interferes with the capture/STT pipeline.
 */

import { useEffect, useRef } from 'react';
import styles from './MicVisualizer.module.css';

interface MicVisualizerProps {
  active: boolean;
  label?: string;
  /** Reuse the capture hook's existing MediaStream so we don't open a second
   *  mic stream (which can interfere with STT capture on some browsers). */
  getStream?: () => MediaStream | null;
}

export function MicVisualizer({ active, label = 'Listening…', getStream }: MicVisualizerProps): JSX.Element | null {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    let raf = 0;
    let audioCtx: AudioContext | null = null;
    let ownStream: MediaStream | null = null;  // only set if we had to open our own

    void (async () => {
      try {
        // Prefer the capture hook's live stream; only open our own as a fallback.
        let stream = getStream?.() ?? null;
        if (!stream) {
          ownStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream = ownStream;
        }
        if (cancelled) {
          ownStream?.getTracks().forEach((t) => t.stop());
          return;
        }
        const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        audioCtx = new Ctor();
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 64;
        analyser.smoothingTimeConstant = 0.7;
        source.connect(analyser);

        const bins = analyser.frequencyBinCount;
        const data = new Uint8Array(bins);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const draw = (): void => {
          raf = requestAnimationFrame(draw);
          analyser.getByteFrequencyData(data);
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          const barW = canvas.width / bins;
          for (let i = 0; i < bins; i++) {
            const h = Math.max(2, (data[i] / 255) * canvas.height);
            ctx.fillStyle = '#0055a5';
            ctx.fillRect(i * barW, (canvas.height - h) / 2, barW * 0.6, h);
          }
        };
        draw();
      } catch {
        // mic unavailable / permission denied — silently render the static pill
      }
    })();

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      // Only stop a stream we opened ourselves — never the capture hook's stream.
      if (ownStream) ownStream.getTracks().forEach((t) => t.stop());
      if (audioCtx) void audioCtx.close().catch(() => undefined);
    };
  }, [active, getStream]);

  if (!active) return null;

  return (
    <div className={styles.visualizer} role="status" aria-live="polite" aria-label={label}>
      <span className={styles.pulse} aria-hidden="true" />
      <span className={styles.label}>{label}</span>
      <canvas ref={canvasRef} width={220} height={36} className={styles.canvas} />
    </div>
  );
}
